import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Dimensions, Pressable, ActivityIndicator, ScrollView, Switch, Platform, Image, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ImprovedAnalyticsService from '../../services/ImprovedAnalyticsService';
import realTimeAnalytics from '../../services/RealTimeAnalyticsAggregator';

// Safely import chart components with error handling
let LineChart, BarChart, PieChart;
try {
  const chartKit = require('react-native-chart-kit');
  // Create wrapped versions of the charts that handle touch events properly
  const createWebCompatibleChart = (ChartComponent) => {
    return React.forwardRef((props, ref) => {
      // Remove touch-specific props when running in web
      const webProps = { ...props };
      
      // List of all React Native touch responder props to remove for web
      const touchProps = [
        'onStartShouldSetResponder',
        'onMoveShouldSetResponder',
        'onResponderGrant',
        'onResponderMove',
        'onResponderRelease',
        'onResponderTerminate',
        'onResponderTerminationRequest',
        'onStartShouldSetResponderCapture',
        'onMoveShouldSetResponderCapture',
        'onResponderEnd',
        'onResponderStart'
      ];
      
      // Remove all touch responder props for web environment
      if (Platform.OS === 'web') {
        touchProps.forEach(prop => {
          if (webProps[prop]) delete webProps[prop];
          if (webProps.style && webProps.style[prop]) delete webProps.style[prop];
        });

        // Add web-specific styles properly
        webProps.style = {
          ...(webProps.style || {}),
          // Replace outline with borderWidth and borderColor
          borderWidth: 0,
          borderColor: 'transparent',
          // Use proper React Native style properties
          userSelect: 'none',
          cursor: 'default'
        };
      }
      
      return <ChartComponent {...webProps} ref={ref} />;
    });
  };
  
  // Wrap all chart components
  LineChart = createWebCompatibleChart(chartKit.LineChart);
  BarChart = createWebCompatibleChart(chartKit.BarChart);
  PieChart = createWebCompatibleChart(chartKit.PieChart);
} catch (error) {
  console.error('Error importing chart components:', error);
  // Create placeholder components
  const PlaceholderChart = ({ children }) => (
    <View style={{ height: 220, backgroundColor: '#f5f5f5', justifyContent: 'center', alignItems: 'center' }}>
      <Text>Chart component not available</Text>
    </View>
  );
  LineChart = PlaceholderChart;
  BarChart = PlaceholderChart;
  PieChart = PlaceholderChart;
}

// Safely import date-fns
let format, subDays, subWeeks, subMonths, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth;
try {
  // Import the entire date-fns library to ensure all functions are available
  const dateFns = require('date-fns');
  
  // Explicitly check if each function exists before assigning
  format = typeof dateFns.format === 'function' ? dateFns.format : null;
  subDays = typeof dateFns.subDays === 'function' ? dateFns.subDays : null;
  subWeeks = typeof dateFns.subWeeks === 'function' ? dateFns.subWeeks : null;
  subMonths = typeof dateFns.subMonths === 'function' ? dateFns.subMonths : null;
  startOfDay = typeof dateFns.startOfDay === 'function' ? dateFns.startOfDay : null;
  endOfDay = typeof dateFns.endOfDay === 'function' ? dateFns.endOfDay : null;
  startOfWeek = typeof dateFns.startOfWeek === 'function' ? dateFns.startOfWeek : null;
  endOfWeek = typeof dateFns.endOfWeek === 'function' ? dateFns.endOfWeek : null;
  startOfMonth = typeof dateFns.startOfMonth === 'function' ? dateFns.startOfMonth : null;
  endOfMonth = typeof dateFns.endOfMonth === 'function' ? dateFns.endOfMonth : null;
  
  // Log which functions were successfully imported
  console.log('Date-fns functions loaded:', {
    format: !!format,
    subDays: !!subDays,
    subWeeks: !!subWeeks,
    subMonths: !!subMonths,
    startOfDay: !!startOfDay,
    endOfDay: !!endOfDay,
    startOfWeek: !!startOfWeek,
    endOfWeek: !!endOfWeek,
    startOfMonth: !!startOfMonth,
    endOfMonth: !!endOfMonth
  });
} catch (error) {
  console.error('Error importing date-fns:', error);
}

// Define fallback functions regardless of whether the import succeeded
// This ensures we always have these functions available
if (!format) format = (date, formatStr) => date.toLocaleDateString();
if (!subDays) subDays = (date, days) => new Date(date.getTime() - days * 24 * 60 * 60 * 1000);
if (!subWeeks) subWeeks = (date, weeks) => new Date(date.getTime() - weeks * 7 * 24 * 60 * 60 * 1000);
if (!subMonths) subMonths = (date, months) => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() - months);
  return newDate;
};
if (!startOfDay) startOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
if (!endOfDay) endOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};
if (!startOfWeek) startOfWeek = (date, options = { weekStartsOn: 0 }) => {
  try {
    if (!date) return new Date();
    const newDate = new Date(date);
    const day = newDate.getDay();
    const weekStartsOn = options && options.weekStartsOn !== undefined ? options.weekStartsOn : 0;
    const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
    newDate.setDate(newDate.getDate() - diff);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  } catch (err) {
    console.error('Error in startOfWeek fallback:', err);
    // Return current date as absolute fallback
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
};
if (!endOfWeek) endOfWeek = (date, options = { weekStartsOn: 0 }) => {
  try {
    if (!date) return new Date();
    const newDate = new Date(date);
    const day = newDate.getDay();
    const weekStartsOn = options && options.weekStartsOn !== undefined ? options.weekStartsOn : 0;
    const diff = 6 - day + weekStartsOn;
    newDate.setDate(newDate.getDate() + diff);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  } catch (err) {
    console.error('Error in endOfWeek fallback:', err);
    // Return current date as absolute fallback
    const fallback = new Date();
    fallback.setHours(23, 59, 59, 999);
    return fallback;
  }
};
if (!startOfMonth) startOfMonth = (date) => {
  const newDate = new Date(date);
  newDate.setDate(1);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
if (!endOfMonth) endOfMonth = (date) => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + 1);
  newDate.setDate(0);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;

const chartConfig = {
  backgroundColor: '#ffffff',
  backgroundGradientFrom: '#ffffff',
  backgroundGradientTo: '#ffffff',
  decimalPlaces: 0,
  color: (opacity = 1) => `rgba(44, 110, 73, ${opacity})`,
  labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
  style: {
    borderRadius: 16,
  },
  propsForDots: {
    r: '6',
    strokeWidth: '2',
    stroke: '#4c956c',
  },
};

const metricDescriptions = {
  totalUsers: "Total number of registered users on MyTassel",
  newUsers: "Users who registered in the last 24 hours",
  activeUsers: "Users who logged in within the last 24 hours",
  pageViews: "Total page views across all MyTassel pages in the last 24 hours"
};

const renderMetric = (title, value, description) => (
  <View style={styles.metricContainer}>
    <Text style={styles.metricTitle}>{title}</Text>
    <Text style={styles.metricValue}>{value}</Text>
    <Text style={styles.metricDescription}>{description}</Text>
  </View>
);

// Add default values and type checking for metrics
const safeNumber = (value) => {
  const num = Number(value);
  return !isNaN(num) ? num : 0;
};

// Helper function to parse page information from page name (moved outside component)
const parsePageInfo = (pageName, pageData) => {
  let pageType = 'Unknown';
  let ownerInfo = 'N/A';
  let displayName = pageName || 'Unknown';
  
  // Extract user information from pageData/params
  const userId = pageData?.userId || pageData?.params?.userId;
  const userDisplayName = pageData?.userDisplayName || pageData?.params?.userDisplayName;
  const isAuthenticated = pageData?.isAuthenticated || pageData?.params?.isAuthenticated;

  // Handle different page types with specific user information
  if (pageName) {
    if (pageName.startsWith('Registry-')) {
      pageType = 'Registry';
      const userId = pageName.replace('Registry-', '');
      // Always use User ID for privacy, but extract actual ID from various sources
      const actualUserId = userId || 
        (pageData?.registryOwner) || 
        (pageData?.ownerDisplayName?.match(/User (\w+)/))?.[1] || 
        userId;
      ownerInfo = `User ${actualUserId}`;
      displayName = `${ownerInfo}'s Registry`;
    } else if (pageName.startsWith('Hub-')) {
      pageType = 'Dashboard';
      const userId = pageName.replace('Hub-', '');
      if (userId === 'Anonymous') {
        ownerInfo = 'Anonymous User';
        displayName = 'Anonymous Dashboard';
      } else {
        // Always use User ID for privacy, but extract actual ID from various sources
        const actualUserId = userId || 
          (pageData?.userDisplayName?.match(/User (\w+)/))?.[1] || 
          (pageData?.ownerDisplayName?.match(/User (\w+)/))?.[1] || 
          userId;
        ownerInfo = `User ${actualUserId}`;
        displayName = `${ownerInfo}'s Dashboard`;
      }
    } else if (pageName.startsWith('Events-')) {
      pageType = 'Events';
      const userId = pageName.replace('Events-', '');
      // Always use User ID for privacy, but extract actual ID from various sources
      const actualUserId = userId || 
        (pageData?.eventsOwner) || 
        (pageData?.ownerDisplayName?.match(/User (\w+)/))?.[1] || 
        userId;
      ownerInfo = `User ${actualUserId}`;
      displayName = `${ownerInfo}'s Events`;
    } else if (pageName.startsWith('Checklist-')) {
      pageType = 'Checklist';
      const checklistType = pageName.replace('Checklist-', '');
      ownerInfo = checklistType.charAt(0).toUpperCase() + checklistType.slice(1);
      displayName = `${ownerInfo} Checklist`;
    } else if (pageName === 'admin' || pageName === 'Admin') {
      pageType = 'Admin';
      ownerInfo = 'System';
      displayName = 'Admin Panel';
    } else if (pageName === 'Registry') {
      pageType = 'Registry';
      // Use the new user information from params or fallback to existing methods
      const finalUserId = userId || pageData?.registryOwner;
      const finalUserDisplayName = userDisplayName || (finalUserId ? `User ${finalUserId}` : null);
      
      if (finalUserId || finalUserDisplayName) {
        ownerInfo = finalUserDisplayName || `User ${finalUserId}`;
        displayName = `${finalUserDisplayName || `User ${finalUserId}`}'s Registry`;
      } else if (isAuthenticated) {
        ownerInfo = 'Authenticated User';
        displayName = 'User Registry';
      } else {
        ownerInfo = 'Visitor';
        displayName = 'Registry Browse';
      }
    } else if (pageName === 'Hub') {
      pageType = 'Dashboard';
      // Use the new user information from params
      const finalUserId = userId;
      const finalUserDisplayName = userDisplayName || (finalUserId ? `User ${finalUserId}` : null);
      
      if (finalUserId || finalUserDisplayName) {
        ownerInfo = finalUserDisplayName || `User ${finalUserId}`;
        displayName = `${finalUserDisplayName || `User ${finalUserId}`}'s Dashboard`;
      } else if (isAuthenticated) {
        ownerInfo = 'Authenticated User';
        displayName = 'User Dashboard';
      } else {
        ownerInfo = 'Visitor';
        displayName = 'Dashboard Browse';
      }
    } else if (pageName === 'Events' || pageName === 'Announcements') {
      pageType = 'Events';
      // Use the new user information from params or fallback to existing methods
      const finalUserId = userId || pageData?.eventsOwner;
      const finalUserDisplayName = userDisplayName || (finalUserId ? `User ${finalUserId}` : null);
      
      if (finalUserId || finalUserDisplayName) {
        ownerInfo = finalUserDisplayName || `User ${finalUserId}`;
        displayName = `${finalUserDisplayName || `User ${finalUserId}`}'s Events`;
      } else if (isAuthenticated) {
        ownerInfo = 'Authenticated User';
        displayName = 'User Events';
      } else {
        ownerInfo = 'Visitor';
        displayName = 'Events Browse';
      }
    } else if (pageName === 'Profile') {
      pageType = 'Profile';
      ownerInfo = 'User Profile';
      displayName = 'User Profile';
    } else if (pageName === 'Landing' || pageName === 'My Tassel - Welcome') {
      pageType = 'Landing';
      ownerInfo = 'Public';
      displayName = 'Welcome Page';
    } else if (pageName === 'Catalogue') {
      pageType = 'Catalogue';
      ownerInfo = 'Public';
      displayName = 'Product Catalogue';
    } else if (pageName === 'Blog') {
      pageType = 'Blog';
      ownerInfo = 'Public';
      displayName = 'Blog';
    } else {
      pageType = 'Other';
      ownerInfo = 'System';
      displayName = pageName;
    }
  }

  return { displayName, pageType, ownerInfo };
};

// Helper function to format page display names with custom UIDs
const formatPageDisplayName = (pageName, activity, pageData = {}) => {
  // Extract custom UID from different possible sources (more comprehensive search)
  let customUID = activity.customUID || activity.userCustomUID || pageData.customUID || pageData.uid;
  
  // If no customUID found, try to extract from ownerDisplayName or userDisplayName
  if (!customUID && (activity.ownerDisplayName || activity.userDisplayName)) {
    const displayName = activity.ownerDisplayName || activity.userDisplayName;
    const match = displayName.match(/([A-Z]\d+)/);
    if (match) {
      customUID = match[1];
    }
  }
  
  // Also check pageContext for custom UID patterns
  if (!customUID && activity.pageContext) {
    const match = activity.pageContext.match(/([A-Z]\d+)/);
    if (match) {
      customUID = match[1];
    }
  }
  
  const firebaseUID = activity.userId !== 'anonymous' ? activity.userId : null;
  

  
  // Clean up page name by removing any attached UIDs or extra info
  let cleanPageName = pageName;
  if (pageName) {
    // Remove long Firebase UIDs (like "Hub-tLt5SCkOPER9ZgqObc4FRnOo26Q2")
    cleanPageName = pageName.split('-')[0];
    // Remove short custom UIDs (like "Events-G88336")
    cleanPageName = cleanPageName.replace(/-[A-Z]\d+$/, '');
    // Clean any remaining patterns
    cleanPageName = cleanPageName.trim();
  }
  
  if (!cleanPageName || cleanPageName === 'Unknown Page') {
    return { title: 'Unknown Page', subtitle: null };
  }
  
  if (cleanPageName === 'Admin' || cleanPageName === 'AdminPanel') {
    return { title: 'Admin Panel', subtitle: null };
  } else if (cleanPageName === 'Registry') {
    return { 
      title: 'Registry', 
      subtitle: customUID ? `User ${customUID}` : null 
    };
  } else if (cleanPageName === 'Hub') {
    return { 
      title: 'Hub', 
      subtitle: customUID ? `User ${customUID}` : null 
    };
  } else if (cleanPageName === 'Events' || cleanPageName === 'Announcements') {
    return { 
      title: 'Events', 
      subtitle: customUID ? `User ${customUID}` : null 
    };
  } else if (cleanPageName === 'Profile') {
    return { 
      title: 'User Profile', 
      subtitle: customUID ? `User ${customUID}` : null 
    };
  } else if (cleanPageName === 'Landing' || cleanPageName === 'My Tassel - Welcome') {
    return { title: 'Welcome Page', subtitle: null };
  } else if (cleanPageName === 'Catalogue') {
    return { title: 'Product Catalogue', subtitle: null };
  } else if (cleanPageName === 'Blog') {
    return { title: 'Blog', subtitle: null };
  } else {
    return { title: cleanPageName, subtitle: customUID ? `User ${customUID}` : null };
  }
};

// Helper function for user interactions
const handleViewerTypePress = (activity, setSelectedUser, setShowUserModal) => {
  // Use custom UID if available, otherwise use the original userId
  const displayUserId = activity.customUID || activity.userCustomUID || activity.userId || 'Anonymous';
  
  const userInfo = {
    userId: displayUserId,
    userDisplayName: activity.userDisplayName || activity.ownerDisplayName || activity.pageContext || (activity.userId !== 'anonymous' ? `User ${activity.userId}` : 'Anonymous Visitor'),
    pageName: activity.pageName,
    pageContext: activity.pageContext,
    viewerType: activity.viewerType,
    isOwner: activity.viewerType === 'owner',
    timestamp: activity.timestamp,
    sessionId: activity.sessionId
  };
  setSelectedUser(userInfo);
  setShowUserModal(true);
};

const Analytics = () => {
  // State for time period selection
  const [timePeriod, setTimePeriod] = useState('week');
  
  // State for analytics data
  const [totalUsers, setTotalUsers] = useState(0);
  const [newUsers, setNewUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [pageViews, setPageViews] = useState(0);
  const [avgSessionDuration, setAvgSessionDuration] = useState(0);
  const [bounceRate, setBounceRate] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [popularPages, setPopularPages] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  
  // Real-time data state
  const [realTimeData, setRealTimeData] = useState({
    activeUsers: 0,
    activeSessions: 0,
    todayPageViews: 0,
    todayUniqueVisitors: 0,
    recentActivity: [],
    lastUpdated: null
  });
  
  // Add error state
  const [error, setError] = useState(null);
  
  // State for loading
  const [loading, setLoading] = useState(true);
  const [realTimeLoading, setRealTimeLoading] = useState(true);
  
  // Add pagination state for registry items
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Add state for user detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Add a safe wrapper for date range functions
  const safeGetDateRange = (rangeType, now) => {
    try {
      switch (rangeType) {
        case 'day':
          return {
            start: startOfDay(now),
            end: endOfDay(now),
            label: 'Today',
            previous: {
              start: startOfDay(subDays(now, 1)),
              end: endOfDay(subDays(now, 1))
            }
          };
        case 'week':
          return {
            start: startOfWeek(now, { weekStartsOn: 0 }),
            end: endOfWeek(now, { weekStartsOn: 0 }),
            label: 'This Week',
            previous: {
              start: startOfWeek(subWeeks(now, 1), { weekStartsOn: 0 }),
              end: endOfWeek(subWeeks(now, 1), { weekStartsOn: 0 })
            }
          };
        case 'month':
          return {
            start: startOfMonth(now),
            end: endOfMonth(now),
            label: 'This Month',
            previous: {
              start: startOfMonth(subMonths(now, 1)),
              end: endOfMonth(subMonths(now, 1))
            }
          };
        case 'quarter':
          return {
            start: startOfMonth(subMonths(now, 3)),
            end: endOfMonth(now),
            label: 'Last 3 Months',
            previous: {
              start: startOfMonth(subMonths(now, 4)),
              end: endOfMonth(subMonths(now, 3))
            }
          };
        case 'year':
          return {
            start: startOfMonth(subMonths(now, 12)),
            end: endOfMonth(now),
            label: 'Last 12 Months',
            previous: {
              start: startOfMonth(subMonths(now, 24)),
              end: endOfMonth(subMonths(now, 12))
            }
          };
        default:
          return {
            start: startOfDay(now),
            end: endOfDay(now),
            label: 'Today',
            previous: {
              start: startOfDay(subDays(now, 1)),
              end: endOfDay(subDays(now, 1))
            }
          };
      }
    } catch (error) {
      console.error(`Error getting date range for ${rangeType}:`, error);
      // Return a safe fallback
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      return {
        start: today,
        end: today,
        label: 'Today (Fallback)',
        previous: {
          start: yesterday,
          end: yesterday
        }
      };
    }
  };
  
  // Real-time data update handler
  const handleRealTimeUpdate = useCallback((type, data) => {
    setRealTimeData(prev => ({
      ...prev,
      ...data
    }));
    
    if (type === 'realTimeStats' || type === 'activeUsers') {
      setRealTimeLoading(false);
    }
  }, []);

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    setCurrentPage(1);

    try {
      const now = new Date();
      const { start, end } = safeGetDateRange(timePeriod, now);

      console.log('Fetching analytics data for period:', timePeriod, 'from', start, 'to', end);

      // Use improved analytics service
      const [
        uniqueVisitors,
        accuratePageViews,
        sessionMetrics,
        popularPagesData,
        popularRegistryItems
      ] = await Promise.all([
        ImprovedAnalyticsService.getAnalyticsData.getTotalUniqueVisitors(start, end),
        ImprovedAnalyticsService.getAnalyticsData.getAccuratePageViews(start, end),
        ImprovedAnalyticsService.getAnalyticsData.getSessionMetrics(start, end),
        ImprovedAnalyticsService.getAnalyticsData.getPopularPages(start, end, 10),
        ImprovedAnalyticsService.getAnalyticsData.getPopularRegistryItems(start, end)
      ]);

      // Update state with improved data
      setTotalUsers(safeNumber(sessionMetrics.uniqueUsers));
      setNewUsers(safeNumber(realTimeData.todayNewUsers));
      setActiveUsers(safeNumber(sessionMetrics.uniqueVisitors));
      setPageViews(safeNumber(accuratePageViews));
      setAvgSessionDuration(safeNumber(sessionMetrics.avgDuration));
      setBounceRate(safeNumber(sessionMetrics.bounceRate));
      setConversionRate(safeNumber(0)); // Implement conversion tracking separately
      setPopularPages(Array.isArray(popularPagesData) ? popularPagesData : []);
      setPopularItems(Array.isArray(popularRegistryItems) ? popularRegistryItems : []);
      setDailyStats([]); // Will be populated by real-time updates

      console.log('Analytics data updated:', {
        uniqueUsers: sessionMetrics.uniqueUsers,
        uniqueVisitors: sessionMetrics.uniqueVisitors,
        pageViews: accuratePageViews,
        avgDuration: sessionMetrics.avgDuration,
        bounceRate: sessionMetrics.bounceRate,
        registryItemsCount: popularRegistryItems?.length || 0
      });

    } catch (error) {
      console.error('Error in analytics data fetching process:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Format time in minutes and seconds
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}m ${seconds}s`;
  };
  
  // Prepare chart data for daily stats
  const prepareChartData = () => {
    if (dailyStats.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            color: (opacity = 1) => `rgba(44, 110, 73, ${opacity})`,
            strokeWidth: 2,
          },
        ],
      };
    }
    
    const labels = dailyStats.map(stat => format(new Date(stat.date), 'MM/dd'));
    const pageViewsData = dailyStats.map(stat => stat.pageViews);
    const activeUsersData = dailyStats.map(stat => stat.activeUserCount);
    const newUsersData = dailyStats.map(stat => stat.newUserCount);
    
    return {
      labels,
      datasets: [
        {
          data: pageViewsData,
          color: (opacity = 1) => `rgba(44, 110, 73, ${opacity})`,
          strokeWidth: 2,
        },
        {
          data: activeUsersData,
          color: (opacity = 1) => `rgba(65, 105, 225, ${opacity})`,
          strokeWidth: 2,
        },
        {
          data: newUsersData,
          color: (opacity = 1) => `rgba(255, 99, 71, ${opacity})`,
          strokeWidth: 2,
        },
      ],
      legend: ['Page Views', 'Active Users', 'New Users'],
    };
  };
  
  // Prepare bar chart data for popular pages
  const preparePopularPagesData = () => {
    // Ensure popularPages is an array and has items
    if (!Array.isArray(popularPages) || popularPages.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
          },
        ],
      };
    }
    
    // Keep only top 5 for chart visualization (top 10 in table)
    const topPages = popularPages.slice(0, 5);
    const labels = topPages.map(page => {
      const name = (page.pagePath || page.pageName || '').replace(/^\//, '').split('/')[0];
      return name || 'Home';
    });
    const data = topPages.map(page => page.views || page.count || 0);
    
    return {
      labels,
      datasets: [
        {
          data,
        },
      ],
    };
  };
  
  // Prepare pie chart data for popular registry items
  const preparePopularItemsData = () => {
    if (popularItems.length === 0) {
      return [];
    }
    
    // Keep only top 5 for chart visualization (top 10 in table)
    return popularItems.slice(0, 5).map((item, index) => {
      const colors = [
        '#4c956c',
        '#2c6e49',
        '#ffc857',
        '#e84855',
        '#2d3047'
      ];
      
      return {
        name: item.name.substring(0, 15) + (item.name.length > 15 ? '...' : ''),
        count: item.views || 0,
        color: colors[index % colors.length],
        legendFontColor: '#7F7F7F',
        legendFontSize: 12,
      };
    });
  };
  
  // Add pagination controls component
  const PaginationControls = ({ totalItems, currentPage, setCurrentPage, itemsPerPage }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    return (
      <View style={styles.tablePagination}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
          <Pressable
            key={pageNum}
            style={[
              styles.paginationButton,
              currentPage === pageNum && styles.paginationButtonActive,
            ]}
            onPress={() => setCurrentPage(pageNum)}
          >
            <Text
              style={[
                styles.paginationText,
                currentPage === pageNum && styles.paginationTextActive,
              ]}
            >
              {pageNum}
            </Text>
          </Pressable>
        ))}
      </View>
    );
  };

  // Calculate current items to display based on pagination
  const getCurrentItems = () => {
    if (!popularItems || popularItems.length === 0) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    // No longer slice the data to 5 items here
    return popularItems.slice(startIndex, endIndex);
  };
  
  // Initialize real-time analytics
  useEffect(() => {
    const initRealTime = async () => {
      try {
        await realTimeAnalytics.init();
        const unsubscribe = realTimeAnalytics.subscribe(handleRealTimeUpdate);
        
        // Get initial cached data
        const currentData = realTimeAnalytics.getCurrentData();
        setRealTimeData(prev => ({
          ...prev,
          ...currentData
        }));
        setRealTimeLoading(false);
        
        return () => {
          unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing real-time analytics:', error);
        setRealTimeLoading(false);
      }
    };
    
    const cleanup = initRealTime();
    
    return () => {
      cleanup.then(fn => fn && fn());
    };
  }, [handleRealTimeUpdate]);

  // Effect to fetch data when time period changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [timePeriod]);
  
  // Update the chart rendering functions
  const renderLineChart = () => {
    try {
      return (
        <LineChart
          data={prepareChartData()}
          width={isMobile ? width - 40 : width - 100}
          height={220}
          chartConfig={{
            ...chartConfig,
            useShadowColorFromDataset: true,
            propsForLabels: {
              fontSize: 12,
            }
          }}
          bezier
          style={{
            ...styles.chart,
            borderRadius: 16,
            padding: 10,
          }}
          withInnerLines={false}
          withOuterLines={true}
          fromZero={true}
        />
      );
    } catch (error) {
      console.error('Error rendering LineChart:', error);
      return (
        <View style={styles.chartPlaceholder}>
          <Text style={styles.chartPlaceholderText}>Chart could not be rendered</Text>
        </View>
      );
    }
  };

  const renderBarChart = () => {
    try {
      return (
        <BarChart
          data={preparePopularPagesData()}
          width={isMobile ? width - 40 : (width - 120) / 2}
          height={220}
          chartConfig={{
            ...chartConfig,
            useShadowColorFromDataset: true,
            propsForLabels: {
              fontSize: 12,
            }
          }}
          style={{
            ...styles.chart,
            borderRadius: 16,
            padding: 10,
          }}
          verticalLabelRotation={30}
          showValuesOnTopOfBars={true}
          fromZero={true}
        />
      );
    } catch (error) {
      console.error('Error rendering BarChart:', error);
      return (
        <View style={styles.chartPlaceholder}>
          <Text style={styles.chartPlaceholderText}>Chart could not be rendered</Text>
        </View>
      );
    }
  };

  const renderPieChart = () => {
    try {
      return (
        <PieChart
          data={preparePopularItemsData()}
          width={isMobile ? width - 40 : (width - 120) / 2}
          height={220}
          chartConfig={{
            ...chartConfig,
            useShadowColorFromDataset: true,
            propsForLabels: {
              fontSize: 12,
            }
          }}
          accessor="count"
          backgroundColor="transparent"
          paddingLeft="15"
          absolute
          style={{
            ...styles.chart,
            borderRadius: 16,
            padding: 10,
          }}
        />
      );
    } catch (error) {
      console.error('Error rendering PieChart:', error);
      return (
        <View style={styles.chartPlaceholder}>
          <Text style={styles.chartPlaceholderText}>Chart could not be rendered</Text>
        </View>
      );
    }
  };
  
  const renderPopularItems = (items) => {
    if (!items || items.length === 0) {
      return (
        <View style={styles.emptyStateContainer}>
          <Text style={styles.emptyStateText}>No registry items data available for the selected time period.</Text>
          <Text style={styles.emptyStateSubtext}>Items will appear here once they receive views or purchases.</Text>
        </View>
      );
    }

    return (
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerCell}>#</Text>
          <Text style={styles.headerCell}>Item</Text>
          <Text style={styles.headerCell}>Category</Text>
          <Text style={styles.headerCell}>Views</Text>
          <Text style={styles.headerCell}>Purchases</Text>
          <Text style={styles.headerCell}>Price</Text>
        </View>
        {items.map((item, index) => (
          <View key={index} style={styles.tableRow}>
            <Text style={styles.tableCell}>{index + 1}</Text>
            <View style={styles.itemCell}>
              {item.image && (
                <Image source={{ uri: item.image }} style={styles.itemThumbnail} />
              )}
              <Text style={styles.itemName}>{item.name}</Text>
            </View>
            <Text style={styles.tableCell}>{item.category}</Text>
            <Text style={styles.tableCell}>{item.views}</Text>
            <Text style={styles.tableCell}>{item.purchases}</Text>
            <Text style={styles.tableCell}>{item.price}</Text>
          </View>
        ))}
      </View>
    );
  };

  const renderPopularPages = (pages) => {
    if (!pages || pages.length === 0) {
      return (
        <View style={styles.emptyStateContainer}>
          <Text style={styles.emptyStateText}>No page view data available for the selected time period.</Text>
          <Text style={styles.emptyStateSubtext}>Pages will appear here once they receive views.</Text>
        </View>
      );
    }



    return (
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerCell}>#</Text>
          <Text style={styles.headerCell}>Page</Text>
          <Text style={styles.headerCell}>Type</Text>
          <Text style={styles.headerCell}>Context</Text>
          <Text style={styles.headerCell}>Views</Text>
          <Text style={styles.headerCell}>Unique Visitors</Text>
        </View>
        {pages.map((page, index) => {
          // Handle both old and new data structures
          const pageName = page.name || page.pagePath || 'Unknown';
          const views = page.count || page.views || 0;
          const uniqueVisitors = page.uniqueVisitors || 'N/A';
          
          // Use the specific page name from analytics data instead of generic names
          const actualPageName = page.specificPage || page.pageName || pageName;
          
          // Format page display with custom UIDs
          const pageDisplay = formatPageDisplayName(actualPageName, {
            customUID: page.userCustomUID || page.ownerDisplayName?.match(/User ([A-Z]\d+)/)?.[1],
            userId: page.userId,
            userCustomUID: page.userCustomUID
          });
          
          // Determine page type for display
          let pageType = 'Other';
          const cleanName = actualPageName?.split('-')[0]?.replace(/-[A-Z]\d+$/, '')?.trim();
          if (cleanName === 'Admin') pageType = 'Admin';
          else if (cleanName === 'Registry') pageType = 'Registry';
          else if (cleanName === 'Hub') pageType = 'Dashboard';
          else if (cleanName === 'Events') pageType = 'Events';
          else if (cleanName === 'Profile') pageType = 'Profile';
          else if (cleanName === 'Landing') pageType = 'Landing';
          
          return (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCell}>{index + 1}</Text>
              <Text style={styles.tableCell}>{pageDisplay.title}</Text>
              <Text style={styles.tableCell}>{pageType}</Text>
              <Text style={styles.tableCell}>{pageDisplay.subtitle || 'System'}</Text>
              <Text style={styles.tableCell}>{views}</Text>
              <Text style={styles.tableCell}>{uniqueVisitors}</Text>
            </View>
          );
        })}
      </View>
    );
  };
  
  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Analytics Dashboard</Text>
        <Text style={styles.subtitle}>{safeGetDateRange(timePeriod, new Date()).label}</Text>
      </View>
      
      <View style={styles.timePeriodSelector}>
        <Pressable
          style={[styles.timePeriodButton, timePeriod === 'day' && styles.activeTimePeriod]}
          onPress={() => setTimePeriod('day')}
        >
          <Text style={[styles.timePeriodText, timePeriod === 'day' && styles.activeTimePeriodText]}>Day</Text>
        </Pressable>
        <Pressable
          style={[styles.timePeriodButton, timePeriod === 'week' && styles.activeTimePeriod]}
          onPress={() => setTimePeriod('week')}
        >
          <Text style={[styles.timePeriodText, timePeriod === 'week' && styles.activeTimePeriodText]}>Week</Text>
        </Pressable>
        <Pressable
          style={[styles.timePeriodButton, timePeriod === 'month' && styles.activeTimePeriod]}
          onPress={() => setTimePeriod('month')}
        >
          <Text style={[styles.timePeriodText, timePeriod === 'month' && styles.activeTimePeriodText]}>Month</Text>
        </Pressable>
        <Pressable
          style={[styles.timePeriodButton, timePeriod === 'quarter' && styles.activeTimePeriod]}
          onPress={() => setTimePeriod('quarter')}
        >
          <Text style={[styles.timePeriodText, timePeriod === 'quarter' && styles.activeTimePeriodText]}>Quarter</Text>
        </Pressable>
        <Pressable
          style={[styles.timePeriodButton, timePeriod === 'year' && styles.activeTimePeriod]}
          onPress={() => setTimePeriod('year')}
        >
          <Text style={[styles.timePeriodText, timePeriod === 'year' && styles.activeTimePeriodText]}>Year</Text>
        </Pressable>
      </View>
      
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#4c956c" />
          <Text style={styles.loadingText}>Loading analytics data...</Text>
        </View>
      ) : (
        <>
          {/* Debugging Panel */}
          <View style={styles.debugSection}>
            <Text style={styles.debugTitle}>Debug Info (Development)</Text>
            <Text style={styles.debugText}>Time Period: {timePeriod}</Text>
            <Text style={styles.debugText}>Real-time Loading: {realTimeLoading ? 'Yes' : 'No'}</Text>
            <Text style={styles.debugText}>Real-time Data Keys: {Object.keys(realTimeData).join(', ')}</Text>
            <Text style={styles.debugText}>Popular Pages Count: {realTimeData.popularPages?.length || 0}</Text>
            <Text style={styles.debugText}>Recent Activity Count: {realTimeData.recentActivity?.length || 0}</Text>
            <Text style={styles.debugText}>Last Updated: {realTimeData.lastUpdated?.toLocaleTimeString() || 'Never'}</Text>
          </View>

          {/* Real-time Metrics Section */}
          <View style={styles.realTimeSection}>
            <View style={styles.realTimeHeader}>
              <Text style={styles.realTimeTitle}>Live Analytics</Text>
              <View style={styles.realTimeIndicator}>
                <View style={styles.liveDot} />
                <Text style={styles.liveText}>LIVE</Text>
              </View>
            </View>
            
            {realTimeLoading ? (
              <View style={styles.realTimeLoading}>
                <ActivityIndicator size="small" color="#4c956c" />
                <Text style={styles.realTimeLoadingText}>Connecting to real-time data...</Text>
              </View>
            ) : (
              <View style={styles.realTimeGrid}>
                <View style={styles.realTimeCard}>
                  <Text style={styles.realTimeValue}>{realTimeData.activeUsers}</Text>
                  <Text style={styles.realTimeLabel}>Active Now</Text>
                </View>
                <View style={styles.realTimeCard}>
                  <Text style={styles.realTimeValue}>{realTimeData.activeSessions}</Text>
                  <Text style={styles.realTimeLabel}>Active Sessions</Text>
                </View>
                <View style={styles.realTimeCard}>
                  <Text style={styles.realTimeValue}>{realTimeData.todayPageViews}</Text>
                  <Text style={styles.realTimeLabel}>Today's Views</Text>
                </View>
                <View style={styles.realTimeCard}>
                  <Text style={styles.realTimeValue}>{realTimeData.todayUniqueVisitors}</Text>
                  <Text style={styles.realTimeLabel}>Unique Visitors</Text>
                </View>
              </View>
            )}
            
            {realTimeData.lastUpdated && (
              <Text style={styles.lastUpdated}>
                Last updated: {realTimeData.lastUpdated.toLocaleTimeString()}
              </Text>
            )}
          </View>

          <View style={styles.metricsContainer}>
            {renderMetric("Total Users", totalUsers, "Users in selected time period")}
            {renderMetric("New Users", newUsers, "New user registrations")}
            {renderMetric("Active Users", activeUsers, "Unique active users")}
            {renderMetric("Page Views", pageViews, "Total page views")}
          </View>
          
          <View style={styles.statsGrid}>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{totalUsers}</Text>
              <Text style={styles.statLabel}>Total Users</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{newUsers}</Text>
              <Text style={styles.statLabel}>New Users</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{activeUsers}</Text>
              <Text style={styles.statLabel}>Active Users</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{pageViews}</Text>
              <Text style={styles.statLabel}>Page Views</Text>
            </View>
          </View>
          
          <View style={styles.advancedStatsGrid}>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{formatTime(safeNumber(avgSessionDuration))}</Text>
              <Text style={styles.statLabel}>Avg. Session Duration</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{safeNumber(bounceRate).toFixed(1)}%</Text>
              <Text style={styles.statLabel}>Bounce Rate</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{safeNumber(conversionRate).toFixed(1)}%</Text>
              <Text style={styles.statLabel}>Conversion Rate</Text>
            </View>
          </View>
          
          <View style={styles.chartContainer}>
            <Text style={styles.chartTitle}>User Activity Over Time</Text>
            {dailyStats.length > 0 ? (
              <View style={styles.chartWrapper}>
                {renderLineChart()}
              </View>
            ) : (
              <View style={styles.noDataContainer}>
                <Text style={styles.noDataText}>No data available for the selected time period</Text>
              </View>
            )}
          </View>
          
          <View style={styles.chartsRow}>
            <View style={[styles.chartContainer, styles.halfChart]}>
              <Text style={styles.chartTitle}>Popular Pages (Live)</Text>
              {realTimeData.popularPages && realTimeData.popularPages.length > 0 ? (
                <View style={styles.popularPagesContainer}>
                  {realTimeData.popularPages.slice(0, 8).map((page, index) => {
                    // Format page name for popular pages
                    const pageDisplay = formatPageDisplayName(page.pageName || page.name, {
                      customUID: page.userCustomUID || page.ownerDisplayName?.match(/User ([A-Z]\d+)/)?.[1],
                      userId: page.userId,
                      userCustomUID: page.userCustomUID
                    });
                    
                    return (
                      <View key={index} style={styles.popularPageItem}>
                        <Text style={styles.popularPageName}>
                          {index + 1}. {pageDisplay.title}
                        </Text>
                        {pageDisplay.subtitle && (
                          <Text style={styles.popularPageSubtitle}>{pageDisplay.subtitle}</Text>
                        )}
                        <Text style={styles.popularPageViews}>{page.count} views</Text>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.emptyStateContainer}>
                  <Text style={styles.emptyStateText}>No live page data available</Text>
                  <Text style={styles.emptyStateSubtext}>Visit some pages to see live tracking</Text>
                </View>
              )}
            </View>
            
            <View style={[styles.chartContainer, styles.halfChart]}>
              <Text style={styles.chartTitle}>Recent Activity</Text>
              {realTimeData.recentActivity && realTimeData.recentActivity.length > 0 ? (
                <ScrollView style={styles.recentActivityContainer} showsVerticalScrollIndicator={false}>
                  {realTimeData.recentActivity.slice(0, 15).map((activity, index) => {
                    // Format page display name with custom UIDs
                    const pageDisplay = formatPageDisplayName(activity.pageName, activity);
                    
                    return (
                      <View key={index} style={styles.activityItem}>
                        <View style={styles.activityInfo}>
                          <Text style={styles.activityPage}>{pageDisplay.title}</Text>
                          {pageDisplay.subtitle && (
                            <Text style={styles.activityContext}>{pageDisplay.subtitle}</Text>
                          )}
                          <Text style={styles.activityTime}>
                            {activity.timestamp.toLocaleTimeString()}
                          </Text>
                        </View>
                        <View style={styles.activityUserType}>
                          <Pressable 
                            onPress={() => handleViewerTypePress(activity, setSelectedUser, setShowUserModal)}
                            style={styles.iconButton}
                          >
                            <Ionicons 
                              name="eye" 
                              size={16} 
                              color="#FF9800" 
                              style={styles.viewerTypeIcon}
                            />
                          </Pressable>
                        </View>
                      </View>
                    );
                  })}
                </ScrollView>
              ) : (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>No recent activity</Text>
                </View>
              )}
            </View>
          </View>
          
          <View style={styles.tableContainer}>
            <Text style={styles.tableTitle}>Top Registry Items (Top 10)</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>#</Text>
              <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Item</Text>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Category</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Views</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Purchases</Text>
            </View>
            {getCurrentItems().map((item, index) => (
              <View key={item.id} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 1 }]}>
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </Text>
                <Text style={[styles.tableCell, { flex: 3 }]}>{item.name}</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>{item.category}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{item.views}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{item.purchaseCount || 0}</Text>
              </View>
            ))}
            <PaginationControls
              totalItems={popularItems.length}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
            />
          </View>
          
          <View style={styles.tableContainer}>
            <Text style={styles.tableTitle}>Top Pages (Top {(realTimeData.popularPages || popularPages).length})</Text>
            {renderPopularPages(realTimeData.popularPages && realTimeData.popularPages.length > 0 ? realTimeData.popularPages : popularPages)}
          </View>
        </>
      )}
      
      {/* User Detail Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showUserModal}
        onRequestClose={() => setShowUserModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>User Details</Text>
              <Pressable
                onPress={() => setShowUserModal(false)}
                style={styles.closeButton}
              >
                <Ionicons name="close" size={24} color="#666" />
              </Pressable>
            </View>
            
            {selectedUser && (
              <ScrollView style={styles.modalBody}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>User ID:</Text>
                  <Text style={styles.detailValue}>{selectedUser.userId}</Text>
                </View>
                
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Display Name:</Text>
                  <Text style={styles.detailValue}>{selectedUser.userDisplayName}</Text>
                </View>
                
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Page:</Text>
                  <Text style={styles.detailValue}>{selectedUser.pageName}</Text>
                </View>
                
                {selectedUser.pageContext && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Context:</Text>
                    <Text style={styles.detailValue}>{selectedUser.pageContext}</Text>
                  </View>
                )}
                
                {selectedUser.viewerType && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Viewer Type:</Text>
                    <Text style={styles.detailValue}>
                      {selectedUser.isOwner ? 'Page Owner' : 'Visitor'}
                    </Text>
                  </View>
                )}
                
                {selectedUser.sessionId && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Session ID:</Text>
                    <Text style={styles.detailValue}>{selectedUser.sessionId}</Text>
                  </View>
                )}
                
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Timestamp:</Text>
                  <Text style={styles.detailValue}>
                    {selectedUser.timestamp ? selectedUser.timestamp.toLocaleString() : 'N/A'}
                  </Text>
                </View>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: isMobile ? 10 : 20,
    backgroundColor: '#f8fafc',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  header: {
    marginBottom: 20,
    width: '100%',
  },
  title: {
    fontSize: isMobile ? 20 : 24,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: isMobile ? 14 : 16,
    color: '#64748b',
  },
  timePeriodSelector: {
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    width: '100%',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
  },
  timePeriodButton: {
    flex: isMobile ? 1 : 0,
    minWidth: isMobile ? '45%' : 'auto',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    margin: isMobile ? 2 : 0,
  },
  activeTimePeriod: {
    backgroundColor: '#4c956c',
  },
  timePeriodText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  activeTimePeriodText: {
    color: '#fff',
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748b',
  },
  statsGrid: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: 10,
    width: '100%',
    marginBottom: 20,
  },
  advancedStatsGrid: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: 10,
    width: '100%',
    marginBottom: 20,
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    flex: isMobile ? 0 : 1,
    minWidth: isMobile ? '100%' : '23%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#4c956c',
    marginBottom: 5,
  },
  statLabel: {
    fontSize: 14,
    color: '#64748b',
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    width: '100%',
    overflow: 'hidden',
  },
  chartsRow: {
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: 20,
  },
  halfChart: {
    width: isMobile ? '100%' : '48%',
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 15,
  },
  chart: {
    marginVertical: 8,
    alignSelf: 'center',
  },
  noDataText: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: 20,
  },
  tableContainer: {
    backgroundColor: '#fefde2',
    borderRadius: 10,
    padding: 15,
    marginVertical: 10,
    width: '100%',
    overflow: 'hidden',
  },
  tableTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
    color: '#1f2937',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#4c956c',
    paddingBottom: 10,
    marginBottom: 10,
    width: '100%',
  },
  headerCell: {
    flex: 1,
    fontSize: isMobile ? 12 : 14,
    fontWeight: 'bold',
    color: '#4c956c',
    paddingRight: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingVertical: 10,
    width: '100%',
  },
  tableCell: {
    flex: 1,
    fontSize: isMobile ? 10 : 12,
    paddingRight: 10,
    flexWrap: 'wrap',
  },
  itemCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  itemThumbnail: {
    width: isMobile ? 20 : 30,
    height: isMobile ? 20 : 30,
    marginRight: 5,
    borderRadius: 5,
  },
  itemName: {
    flex: 1,
    fontSize: isMobile ? 10 : 12,
    flexWrap: 'wrap',
  },
  emptyStateContainer: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#fefde2',
    borderRadius: 10,
    width: '100%',
  },
  emptyStateText: {
    fontSize: isMobile ? 14 : 16,
    color: '#4c956c',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: isMobile ? 12 : 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 5,
  },
  tablePagination: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 16,
    gap: 8,
  },
  paginationButton: {
    padding: '8px 12px',
    borderRadius: 6,
    backgroundColor: '#f1f5f9',
    marginHorizontal: 4,
  },
  paginationButtonActive: {
    backgroundColor: '#4c956c',
  },
  paginationText: {
    color: '#4b5563',
    fontSize: 14,
  },
  paginationTextActive: {
    color: 'white',
  },
  chartWrapper: {
    width: '100%',
    overflow: 'hidden',
  },
  chartPlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chartPlaceholderText: {
    fontSize: 16,
    color: '#64748b',
    marginBottom: 10,
  },
  chartPlaceholderCode: {
    fontSize: 14,
    color: '#4c956c',
    fontWeight: 'bold',
  },
  noDataContainer: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#fefde2',
    borderRadius: 10,
    width: '100%',
  },
  metricsContainer: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
    width: '100%',
  },
  metricContainer: {
    width: isMobile ? '100%' : '23%',
    backgroundColor: '#fefde2',
    borderRadius: 10,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  metricTitle: {
    fontSize: isMobile ? 16 : 20,
    fontWeight: 'bold',
    color: '#4c956c',
    marginBottom: 5,
  },
  metricValue: {
    fontSize: isMobile ? 24 : 32,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  metricDescription: {
    fontSize: isMobile ? 12 : 14,
    color: '#666',
    fontStyle: 'italic',
  },
  // Real-time styles
  realTimeSection: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  realTimeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  realTimeTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  realTimeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#22c55e',
    marginRight: 5,
  },
  liveText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#22c55e',
  },
  realTimeLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  realTimeLoadingText: {
    marginLeft: 10,
    fontSize: 14,
    color: '#64748b',
  },
  realTimeGrid: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: 15,
  },
  realTimeCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 15,
    flex: isMobile ? 0 : 1,
    minWidth: isMobile ? '100%' : '23%',
    alignItems: 'center',
  },
  realTimeValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4c956c',
    marginBottom: 5,
  },
  realTimeLabel: {
    fontSize: 12,
    color: '#64748b',
    textAlign: 'center',
  },
  lastUpdated: {
    fontSize: 12,
    color: '#94a3b8',
    textAlign: 'center',
    marginTop: 10,
    fontStyle: 'italic',
  },
  // Popular pages and activity styles
  popularPagesContainer: {
    flex: 1,
  },
  popularPageItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  popularPageName: {
    fontSize: 14,
    color: '#1e293b',
    flex: 1,
  },
  popularPageSubtitle: {
    fontSize: 11,
    color: '#64748b',
    fontStyle: 'italic',
    flex: 1,
  },
  popularPageViews: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: 'bold',
  },
  recentActivityContainer: {
    maxHeight: 300,
  },
  activityItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  activityInfo: {
    flex: 1,
  },
  activityPage: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
  },
  activityContext: {
    fontSize: 11,
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: 1,
  },
  activityTime: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  activityUserType: {
    marginLeft: 10,
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  userTypeIcon: {
    marginRight: 2,
  },
  viewerTypeIcon: {
    marginLeft: 2,
  },
  // Debug section styles
  debugSection: {
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#ffc107',
  },
  debugTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#856404',
    marginBottom: 10,
  },
  debugText: {
    fontSize: 12,
    color: '#856404',
    marginBottom: 4,
    fontFamily: 'monospace',
  },
  // Icon button styles
  iconButton: {
    padding: 4,
    borderRadius: 4,
    marginHorizontal: 2,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    margin: 20,
    maxHeight: '80%',
    minWidth: isMobile ? '90%' : '50%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    maxHeight: 400,
  },
  detailRow: {
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 14,
    color: '#1e293b',
    backgroundColor: '#f8fafc',
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
});

export default Analytics; 